import { ComponentBase, component } from "@sygnal/sse-core";
import { gsap } from "gsap";

const TABLET_BREAKPOINT = 991;

@component('nav_links')
export class NavLinksComponent extends ComponentBase {
  private isOpen = false;
  private isServicesOpen = false;
  private isMobileServicesOpen = false;

  private trigger: HTMLElement | null = null;
  private popup: HTMLElement | null = null;

  private servicesTrigger: HTMLElement | null = null;
  private servicesPopup: HTMLElement | null = null;
  private servicesChevron: HTMLElement | null = null;
  private servicesCards: HTMLElement[] = [];
  private mobilePopup: HTMLElement | null = null;

  protected async onLoad(): Promise<void> {
    // Contact dropdown
    this.trigger = this.element.querySelector<HTMLElement>(".nav_menu-contact");
    this.popup = this.element.querySelector<HTMLElement>(".nav_contact-component");

    if (this.trigger && this.popup) {
      const popup = this.popup;
      popup.style.display = "none";
      popup.style.opacity = "0";
      popup.style.transform = "translateY(-8px)";
      popup.style.transition = "opacity 0.25s ease, transform 0.25s ease";
      this.trigger.addEventListener("click", this.handleClick.bind(this));
    }

    // Services dropdown — shared trigger for desktop and mobile
    this.servicesTrigger = this.element.querySelector<HTMLElement>(".nav_menu-services-wrapper");
    this.servicesPopup = this.element.querySelector<HTMLElement>(".nav_services-popup-1");
    this.servicesChevron = this.element.querySelector<HTMLElement>(".nav-services_chevron");
    this.servicesCards = Array.from(this.element.querySelectorAll<HTMLElement>(".nav_services-card-1"));
    this.mobilePopup = this.element.querySelector<HTMLElement>(".nav_services-popup-mob");

    if (this.servicesTrigger) {
      this.injectCardHoverStyles();
      if (this.servicesPopup) {
        gsap.set(this.servicesPopup, { display: "none" });
        gsap.set(this.servicesCards, { opacity: 0, y: -10 });
      }
      if (this.mobilePopup) {
        gsap.set(this.mobilePopup, { display: "none", opacity: 0, y: -8 });
      }
      this.servicesTrigger.addEventListener("click", this.handleServicesWrapperClick.bind(this));
    }

    document.addEventListener("click", this.handleOutsideClick.bind(this));
    document.addEventListener("keydown", this.handleKeyDown.bind(this));
  }

  private handleClick(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    if (this.isServicesOpen) { this.isServicesOpen = false; this.hideServices(); }
    this.isOpen = !this.isOpen;
    this.isOpen ? this.show() : this.hide();
  }

  private handleServicesWrapperClick(e: Event): void {
    e.preventDefault();
    e.stopPropagation();
    if (window.innerWidth <= TABLET_BREAKPOINT) {
      this.isMobileServicesOpen = !this.isMobileServicesOpen;
      this.isMobileServicesOpen ? this.showMobileServices() : this.hideMobileServices();
    } else {
      if (this.isOpen) { this.isOpen = false; this.hide(); }
      this.isServicesOpen = !this.isServicesOpen;
      this.isServicesOpen ? this.showServices() : this.hideServices();
    }
  }

  private handleOutsideClick(e: Event): void {
    const target = e.target as Node;
    if (this.isOpen && !this.trigger!.contains(target) && !this.popup!.contains(target)) {
      this.isOpen = false;
      this.hide();
    }
    if (this.isServicesOpen && this.servicesTrigger && this.servicesPopup &&
        !this.servicesTrigger.contains(target) && !this.servicesPopup.contains(target)) {
      this.isServicesOpen = false;
      this.hideServices();
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      if (this.isOpen) { this.isOpen = false; this.hide(); }
      if (this.isServicesOpen) { this.isServicesOpen = false; this.hideServices(); }
      if (this.isMobileServicesOpen) { this.isMobileServicesOpen = false; this.hideMobileServices(); }
    }
  }

  private show(): void {
    const popup = this.popup!;
    popup.style.display = "block";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        popup.style.opacity = "1";
        popup.style.transform = "translateY(0)";
      });
    });
  }

  private hide(): void {
    const popup = this.popup!;
    popup.style.opacity = "0";
    popup.style.transform = "translateY(-8px)";
    setTimeout(() => { popup.style.display = "none"; }, 250);
  }

  private showServices(): void {
    if (this.servicesChevron) {
      gsap.to(this.servicesChevron, { rotation: 180, duration: 0.3, ease: "power2.inOut" });
    }
    gsap.set(this.servicesPopup!, { display: "flex" });
    gsap.to(this.servicesCards, {
      opacity: 1,
      y: 0,
      duration: 0.35,
      ease: "power2.out",
      stagger: 0.08,
    });
  }

  private hideServices(): void {
    if (this.servicesChevron) {
      gsap.to(this.servicesChevron, { rotation: 0, duration: 0.3, ease: "power2.inOut" });
    }
    gsap.to(this.servicesCards, {
      opacity: 0,
      y: -10,
      duration: 0.25,
      ease: "power2.in",
      stagger: 0.05,
      onComplete: () => { gsap.set(this.servicesPopup!, { display: "none" }); },
    });
  }

  private showMobileServices(): void {
    const popup = this.mobilePopup!;
    gsap.set(popup, { display: "block" });
    gsap.to(popup, { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" });
  }

  private hideMobileServices(): void {
    const popup = this.mobilePopup!;
    gsap.to(popup, {
      opacity: 0,
      y: -8,
      duration: 0.25,
      ease: "power2.in",
      onComplete: () => { gsap.set(popup, { display: "none" }); },
    });
  }

  private injectCardHoverStyles(): void {
    const style = document.createElement("style");
    style.textContent = [
      ".nav_services-card-1 { border: 1px solid transparent; transition: border-color 0.2s ease; }",
      ".nav_services-card-1:hover { border-color: rgba(174, 255, 167, 0.3); }",
    ].join(" ");
    document.head.appendChild(style);
  }
}
